// App.js
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import 'bootstrap/dist/css/bootstrap.min.css';
import { AuthProvider, useAuth } from './context';
import RetailOneLogin from './features/auth/RetailOneLogin';
import SBALogin from './features/auth/SBALogin';
import RetailOneDashboard from './pages/Home';
import OTPVerification from './features/auth/OTPVerification';
import InviteUsers from './features/invite/InviteUsers';
import AcceptInvite from './features/invite/AcceptInvite';
import Profile from './features/profile/Profile';
import ForgotPassword from './features/auth/ForgotPassword';
import SetPassword from './features/auth/SetPassword';
import UsersList from './features/users/UsersList';
// 
import Roles from './features/roles/Roles';  // NEW 
import PermissionsPage from './features/permissions/PermissionsPage';
//
// import RolesManager from './features/roles/RolesManager';
import RolePermissionViewer from './features/permissions/RolePermissionViewer';
import AcceptSuperAdminInvite from './features/invite/AcceptSuperAdminInvite';
import AddSale from './pages/Sales/AddSale';  // 

import "./App.css";
import "./styles/dashboard.css";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const ProtectedRoute = ({ children, allowedType }) => {
  const { isAuthenticated, userType, loading } = useAuth();

  if (loading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" />;
  }

  if (allowedType && userType !== allowedType) {
    return <Navigate to="/" />;
  }

  return children;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>

            {/* PUBLIC ROUTES */}
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<SetPassword />} />
            <Route path="/acceptad" element={<AcceptSuperAdminInvite />} />

            {/* LOGIN ROUTES */}
            <Route path="/" element={<RetailOneLogin />} />
            <Route path="/support" element={<SBALogin />} />
            <Route path="/verify-otp" element={<OTPVerification />} />

            {/* ============================================================
                RETAILONE DASHBOARD - All routes under this parent
                ============================================================ */}
            <Route
              path="/retailone-dashboard"
              element={
                <ProtectedRoute allowedType="retailone">
                  <RetailOneDashboard />
                </ProtectedRoute>
              }
            />

            {/* Dashboard pages (rendered inside RetailOneDashboard via children) */}
            <Route
              path="/retailone-dashboard/invite"
              element={
                <ProtectedRoute allowedType="retailone">
                  <RetailOneDashboard>
                    <InviteUsers />
                  </RetailOneDashboard>
                </ProtectedRoute>
              }
            />

            <Route
              path="/retailone-dashboard/users"
              element={
                <ProtectedRoute allowedType="retailone">
                  <RetailOneDashboard>
                    <UsersList />
                  </RetailOneDashboard>
                </ProtectedRoute>
              }
            />

            <Route
              path="/retailone-dashboard/profile"
              element={
                <ProtectedRoute allowedType="retailone">
                  <RetailOneDashboard>
                    <Profile />
                  </RetailOneDashboard>
                </ProtectedRoute>
              }
            />

            {/* ============================================================
                ACCESS CONTROL - Routes (standalone, not inside dashboard)
                ============================================================ */}

            {/* Roles - Combined View + Manage with tabs */}
            <Route
              path="/retailone-dashboard/roles"
              element={
                <ProtectedRoute allowedType="retailone">
                  <RetailOneDashboard>
                    <Roles />  {/* NEW - Replaces RolesList */}
                  </RetailOneDashboard>
                </ProtectedRoute>
              }
            />

            {/* Permissions - Combined Manage + View with tabs */}
            <Route
              path="/retailone-dashboard/permissions"
              element={
                <ProtectedRoute allowedType="retailone">
                  <RetailOneDashboard>
                    <PermissionsPage />
                  </RetailOneDashboard>
                </ProtectedRoute>
              }
            />

            {/* 
              REMOVED: /retailone-dashboard/rolemanager 
              Now integrated into the Roles component via tabs
            */}

            {/* ============================================================
                 ROUTES - Add Sale
                ============================================================ */}
            <Route
              path="/add_sale"
              element={
                <ProtectedRoute allowedType="retailone">
                  <RetailOneDashboard>
                    <AddSale />
                  </RetailOneDashboard>
                </ProtectedRoute>
              }
            />

            {/* ============================================================
                DEV TOOLS - Sanity checks (keep as is)
                ============================================================ */}
            <Route
              path="/dev"
              element={
                <ProtectedRoute allowedType="retailone">
                  <RolePermissionViewer />
                </ProtectedRoute>
              }
            />

          </Routes>
        </AuthProvider>
      </BrowserRouter>

      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;